import { useEffect, useRef, useState } from "react";
import { useLocation, Navigate, Link } from "react-router-dom";
import { Check, ArrowRight, Mail, Phone, Calendar, Users, MapPin, DollarSign, Heart, Star, Sparkles } from "lucide-react";
import { SITE_LOGO } from "@/constants/assets";

// ─── Confetti Canvas ──────────────────────────────────────────────────────────
const CONFETTI_DURATION_MS = 17000; // active burst: 17 s
const CONFETTI_FADEOUT_MS  = 2500;  // fade-out over 2.5 s after that

function ConfettiCanvas() {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const colors = ["#c4646a", "#f9a8d4", "#fbbf24", "#34d399", "#60a5fa", "#a78bfa", "#fb923c", "#f472b6"];
    const shapes = ["circle", "rect", "triangle"];

    const particles = Array.from({ length: 180 }, () => ({
      x:             Math.random() * canvas.width,
      y:             -20 - Math.random() * 200,
      vx:            (Math.random() - 0.5) * 3,
      vy:            2 + Math.random() * 4,
      color:         colors[Math.floor(Math.random() * colors.length)],
      shape:         shapes[Math.floor(Math.random() * shapes.length)],
      size:          6 + Math.random() * 10,
      rotation:      Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.15,
      baseOpacity:   0.85 + Math.random() * 0.15,
      wobble:        Math.random() * Math.PI * 2,
      wobbleSpeed:   0.05 + Math.random() * 0.05,
    }));

    const startTime = performance.now();

    const draw = (now) => {
      const elapsed = now - startTime;

      // Compute a global opacity multiplier for the fade-out phase
      let globalMult = 1;
      if (elapsed > CONFETTI_DURATION_MS) {
        const fadeProgress = (elapsed - CONFETTI_DURATION_MS) / CONFETTI_FADEOUT_MS;
        globalMult = Math.max(0, 1 - fadeProgress);
      }

      // Once fully faded, clear canvas and stop
      if (globalMult <= 0) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        // After CONFETTI_DURATION_MS stop spawning new particles from top
        if (elapsed < CONFETTI_DURATION_MS && p.y > canvas.height + 20) {
          p.y  = -20;
          p.x  = Math.random() * canvas.width;
          p.vy = 2 + Math.random() * 4;
        }

        p.y       += p.vy;
        p.x       += p.vx + Math.sin(p.wobble) * 0.8;
        p.wobble  += p.wobbleSpeed;
        p.rotation += p.rotationSpeed;
        p.vy      += 0.04; // gravity

        // Skip particles that have already fallen off screen during fade-out
        if (p.y > canvas.height + 20) return;

        ctx.save();
        ctx.globalAlpha = p.baseOpacity * globalMult;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;

        if (p.shape === "circle") {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === "rect") {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else {
          ctx.beginPath();
          ctx.moveTo(0, -p.size / 2);
          ctx.lineTo(p.size / 2, p.size / 2);
          ctx.lineTo(-p.size / 2, p.size / 2);
          ctx.closePath();
          ctx.fill();
        }
        ctx.restore();
      });

      animRef.current = requestAnimationFrame(draw);
    };

    // Start after a tiny delay so the page is visible first
    const t = setTimeout(() => { animRef.current = requestAnimationFrame(draw); }, 300);

    return () => {
      clearTimeout(t);
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-10"
      style={{ opacity: 0.85 }}
    />
  );
}

// ─── Celebration Sound ────────────────────────────────────────────────────────
function useCelebrationSound() {
  useEffect(() => {
    let ctx;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch { return; }

    const playNote = (freq, start, duration, type = "sine", vol = 0.18) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(vol, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration + 0.05);
    };

    // Joyful ascending melody
    const melody = [
      [523.25, 0.0], [659.25, 0.15], [783.99, 0.30], [1046.5, 0.45],
      [783.99, 0.65], [1046.5, 0.80], [1318.5, 1.0], [1046.5, 1.2],
      [1318.5, 1.4], [1567.98, 1.6],
    ];
    melody.forEach(([freq, t]) => playNote(freq, t, 0.4, "sine", 0.15));

    // Harmony
    const harmony = [
      [392.0, 0.0], [523.25, 0.15], [659.25, 0.30], [783.99, 0.45],
    ];
    harmony.forEach(([freq, t]) => playNote(freq, t, 0.6, "triangle", 0.08));

    // Sparkle high notes
    [1046.5, 1318.5, 1567.98, 2093].forEach((freq, i) => {
      playNote(freq, 1.8 + i * 0.12, 0.2, "sine", 0.06);
    });

    return () => { try { ctx.close(); } catch {} };
  }, []);
}

// ─── Animated Section ─────────────────────────────────────────────────────────
function AnimatedSection({ children, delay = 0, className = "" }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(30px)",
        transition: "opacity 0.7s ease, transform 0.7s ease",
      }}
    >
      {children}
    </div>
  );
}

// ─── Animated Check Icon ──────────────────────────────────────────────────────
function AnimatedCheck() {
  const [drawn, setDrawn] = useState(false);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setDrawn(true), 400);
    const t2 = setTimeout(() => setPulse(true), 900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div className="flex justify-center mb-8">
      <div
        style={{
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(52,211,153,0.25) 0%, rgba(52,211,153,0.05) 70%)",
          border: "3px solid rgba(52,211,153,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: drawn ? "scale(1)" : "scale(0)",
          transition: "transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
          boxShadow: pulse ? "0 0 40px rgba(52,211,153,0.5), 0 0 80px rgba(52,211,153,0.2)" : "none",
          animation: pulse ? "checkPulse 2s ease-in-out infinite" : "none",
        }}
      >
        <Check
          style={{
            width: 48,
            height: 48,
            color: "#34d399",
            strokeDasharray: 100,
            strokeDashoffset: drawn ? 0 : 100,
            transition: "stroke-dashoffset 0.6s ease 0.3s",
          }}
        />
      </div>
    </div>
  );
}

// ─── Floating Hearts ──────────────────────────────────────────────────────────
function FloatingHearts() {
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${10 + i * 11}%`,
            bottom: "-40px",
            animation: `floatHeart ${4 + i * 0.7}s ease-in-out ${i * 0.5}s infinite`,
            opacity: 0.15 + (i % 3) * 0.1,
            fontSize: `${16 + (i % 3) * 10}px`,
          }}
        >
          ♥
        </div>
      ))}
    </div>
  );
}

// ─── Step Item ────────────────────────────────────────────────────────────────
function StepItem({ number, text, delay }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div
      className="flex items-start gap-3"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateX(0)" : "translateX(-20px)",
        transition: "opacity 0.5s ease, transform 0.5s ease",
      }}
    >
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{
          background: "rgba(196,100,106,0.2)",
          border: "1px solid rgba(196,100,106,0.4)",
        }}
      >
        <span className="text-[#c4646a] text-sm font-medium">{number}</span>
      </div>
      <p className="text-gray-300">{text}</p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ThankYou() {
  const location = useLocation();
  const { submission } = location.state || {};
  useCelebrationSound();

  if (!submission) {
    return <Navigate to="/" replace />;
  }

  const firstName = submission.couple_names?.split("&")[0]?.trim() || "Couple";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col relative overflow-hidden" data-testid="thank-you-page">
      {/* Background glow */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(196,100,106,0.12) 0%, transparent 70%)",
        }}
      />

      <FloatingHearts />
      <ConfettiCanvas />

      {/* Header */}
      <header className="py-6 px-6 flex justify-center relative z-20">
        <AnimatedSection delay={0}>
          <img
            src={SITE_LOGO}
            alt="Inventive Pages"
            className="h-[8rem] object-contain"
          />
        </AnimatedSection>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-8 relative z-20">
        <div className="w-full max-w-2xl text-center">

          {/* Animated Check */}
          <AnimatedSection delay={200}>
            <AnimatedCheck />
          </AnimatedSection>

          {/* Title */}
          <AnimatedSection delay={600}>
            <h1 className="text-3xl md:text-5xl font-light mb-3">
              Thank You,{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #c4646a, #f9a8d4)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {firstName}
              </span>
              !
            </h1>
            <div className="flex justify-center gap-2 mb-4">
              {[Heart, Star, Sparkles, Star, Heart].map((Icon, i) => (
                <Icon
                  key={i}
                  className="w-4 h-4 text-[#c4646a]"
                  style={{
                    animation: `iconBounce 1.5s ease-in-out ${i * 0.15}s infinite`,
                    opacity: 0.7,
                  }}
                />
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={800}>
            <p className="text-gray-400 text-lg mb-8">
              Your wedding inquiry has been submitted successfully.
            </p>
          </AnimatedSection>

          {/* Reference Card */}
          <AnimatedSection delay={1000}>
            <div
              className="rounded-2xl p-6 mb-6"
              style={{
                background: "linear-gradient(135deg, rgba(196,100,106,0.15), rgba(196,100,106,0.05))",
                border: "1px solid rgba(196,100,106,0.3)",
                boxShadow: "0 0 30px rgba(196,100,106,0.1)",
              }}
            >
              <p className="text-gray-400 text-sm mb-2">Your Reference Number</p>
              <p
                className="text-2xl font-mono"
                data-testid="reference-id"
                style={{
                  background: "linear-gradient(135deg, #c4646a, #f9a8d4)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "refGlow 3s ease-in-out infinite",
                }}
              >
                {submission.reference_id}
              </p>
              <p className="text-gray-500 text-sm mt-2">Please save this for future correspondence</p>
            </div>
          </AnimatedSection>

          {/* Summary */}
          <AnimatedSection delay={1200}>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6 text-left">
              <h3 className="text-lg font-medium text-white mb-4 text-center">Submission Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { Icon: Phone, label: "Phone", value: `+${submission.country_code || "91"} ${submission.phone}` },
                  { Icon: Calendar, label: "Wedding Date", value: submission.wedding_date || "-" },
                  { Icon: Users, label: "Guest Count", value: submission.guest_count || "-" },
                  { Icon: MapPin, label: "Destination", value: submission.wedding_destination || "-" },
                ].map(({ Icon, label, value }, i) => (
                  <div
                    key={label}
                    className="flex items-center gap-3"
                    style={{
                      opacity: 0,
                      animation: `fadeSlideIn 0.5s ease ${1.4 + i * 0.15}s forwards`,
                    }}
                  >
                    <Icon className="w-5 h-5 text-[#c4646a] flex-shrink-0" />
                    <div>
                      <p className="text-gray-400 text-xs">{label}</p>
                      <p className="text-white">{value}</p>
                    </div>
                  </div>
                ))}
                <div
                  className="flex items-center gap-3 md:col-span-2"
                  style={{
                    opacity: 0,
                    animation: "fadeSlideIn 0.5s ease 2.0s forwards",
                  }}
                >
                  <DollarSign className="w-5 h-5 text-[#c4646a] flex-shrink-0" />
                  <div>
                    <p className="text-gray-400 text-xs">Budget Range</p>
                    <p className="text-white">{submission.budget_range || "-"}</p>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* What's Next */}
          <AnimatedSection delay={1800}>
            <div
              className="rounded-2xl p-6 mb-6 text-left"
              style={{
                background: "linear-gradient(135deg, rgba(196,100,106,0.08), transparent)",
                border: "1px solid rgba(196,100,106,0.2)",
              }}
            >
              <h3 className="text-lg font-medium text-white mb-4 text-center">What Happens Next?</h3>
              <div className="space-y-3">
                <StepItem delay={2000} number="1" text="Our team will carefully review your wedding vision" />
                <StepItem delay={2200} number="2" text="A dedicated wedding planner will reach out within 24–48 hours" />
                <StepItem delay={2400} number="3" text="We'll schedule a discovery call to understand your dreams better" />
                <StepItem delay={2600} number="4" text="You'll receive a personalized proposal tailored to your celebration" />
              </div>
            </div>
          </AnimatedSection>

          {/* Contact */}
          <AnimatedSection delay={2800}>
            <div className="mb-8">
              <p className="text-gray-400 text-sm mb-4">Questions? Reach out to us:</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="tel:+919898989863" className="flex items-center gap-2 text-[#c4646a] hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                  <span>+91 98989 89863</span>
                </a>
                <a href="mailto:Inventivepages@gmail.com" className="flex items-center gap-2 text-[#c4646a] hover:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                  <span>Inventivepages@gmail.com</span>
                </a>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={3000}>
            <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <span>Submit another inquiry</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </AnimatedSection>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center relative z-20">
        <AnimatedSection delay={3200}>
          <p className="text-[#c4646a] text-sm italic mb-2">
            Your sacred celebration deserves soulful attention.
          </p>
          <Link to="/" className="text-gray-500 text-sm hover:text-white transition-colors">
            www.inventivepages.com
          </Link>
        </AnimatedSection>
      </footer>

      {/* Inline keyframes */}
      <style>{`
        @keyframes floatHeart {
          0%   { transform: translateY(0) rotate(0deg); opacity: 0; }
          10%  { opacity: 0.3; }
          90%  { opacity: 0.15; }
          100% { transform: translateY(-100vh) rotate(20deg); opacity: 0; }
        }
        @keyframes checkPulse {
          0%, 100% { box-shadow: 0 0 30px rgba(52,211,153,0.4), 0 0 60px rgba(52,211,153,0.15); }
          50%       { box-shadow: 0 0 50px rgba(52,211,153,0.7), 0 0 100px rgba(52,211,153,0.3); }
        }
        @keyframes iconBounce {
          0%, 100% { transform: translateY(0) scale(1); }
          50%       { transform: translateY(-5px) scale(1.2); }
        }
        @keyframes refGlow {
          0%, 100% { filter: brightness(1); }
          50%       { filter: brightness(1.4) drop-shadow(0 0 8px rgba(196,100,106,0.8)); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateX(-15px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
